import type { ApplicantWithRelations } from "@sr-partner-portal/common";
import { createContext, useCallback, useContext, useState } from "react";
import type { ApplicantLockInfo } from "@/utilities";

type JobPositionApplicantMap = {
    [jobPositionId: string]: (ApplicantWithRelations & {
        resolved: boolean;
        lock?: ApplicantLockInfo | null;
    })[];
};

interface PaginationSlice {
    jobPositionId: number | undefined;
    jobPositionApplicantMap: JobPositionApplicantMap;
    page: number;
    pendingUpdateIds: number[];
}

const defaultSlice: PaginationSlice = {
    jobPositionId: undefined,
    jobPositionApplicantMap: {},
    page: 1,
    pendingUpdateIds: [],
};

interface ApplicantResolvePaginationContextType {
    slices: Record<string, PaginationSlice>;
    updateSlice: (
        key: string,
        updater: (prev: PaginationSlice) => PaginationSlice,
    ) => void;
}

const ApplicantResolvePaginationContext =
    createContext<ApplicantResolvePaginationContextType>({
        slices: {},
        updateSlice: () => undefined,
    });

const ApplicantResolvePaginationProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [slices, setSlices] = useState<Record<string, PaginationSlice>>({});

    const updateSlice = useCallback(
        (key: string, updater: (prev: PaginationSlice) => PaginationSlice) => {
            setSlices((prev) => {
                const previousSlice = prev[key] ?? defaultSlice;
                const nextSlice = updater(previousSlice);

                // Updaters may hand back the previous slice to signal "nothing
                // changed"; keep the state identity so nobody re-renders.
                if (nextSlice === previousSlice) {
                    return prev;
                }

                return { ...prev, [key]: nextSlice };
            });
        },
        [],
    );

    return (
        <ApplicantResolvePaginationContext.Provider
            value={{ slices, updateSlice }}
        >
            {children}
        </ApplicantResolvePaginationContext.Provider>
    );
};

const useApplicantResolvePaginationContext = () =>
    useContext(ApplicantResolvePaginationContext);

export {
    ApplicantResolvePaginationContext,
    ApplicantResolvePaginationProvider,
    defaultSlice,
    useApplicantResolvePaginationContext,
};
export type { JobPositionApplicantMap, PaginationSlice };
